import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Grid2 as Grid,
  Snackbar,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import VerifiedIcon from '@mui/icons-material/Verified';
import { motion } from 'framer-motion';
import logo from '../../assets/full_logo.png';
import axios from 'axios';

const MotionBox = motion(Box);

interface HeroSectionProps {
  scrollToContact: () => void;
}

const HeroSection = React.forwardRef<HTMLDivElement, HeroSectionProps>(
  ({ scrollToContact }, ref) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [email, setEmail] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [snackbarOpen, setSnackbarOpen] = React.useState(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState('');
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<
      'success' | 'error'
    >('success');

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (validateEmail(email)) {
        setLoading(true);

        axios
          .post(process.env.REACT_APP_MAILING_LIST_ENDPOINT || '', {
            email: email,
          })
          .then(() => {
            setSnackbarSeverity('success');
            setSnackbarMessage(
              'You have successfully joined our mailing list!',
            );
            setSnackbarOpen(true);
            setEmail('');
          })
          .catch((error) => {
            console.error(error);
            setSnackbarSeverity('error');
            setSnackbarMessage('Failed to subscribe. Please try again later.');
            setSnackbarOpen(true);
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        setSnackbarSeverity('error');
        setSnackbarMessage('Please enter a valid email address.');
        setSnackbarOpen(true);
      }
    };

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(event.target.value);
    };

    const validateEmail = (email: string) => {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    };

    const handleSnackbarClose = () => {
      setSnackbarOpen(false);
    };

    // Animation variants
    const containerVariants = {
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: 0.2,
        },
      },
    };

    const itemVariants = {
      hidden: { y: 20, opacity: 0 },
      visible: {
        y: 0,
        opacity: 1,
        transition: {
          type: 'spring',
          stiffness: 100,
        },
      },
    };

    return (
      <Box
        ref={ref}
        sx={{
          pt: { xs: 8, sm: 10 },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }} sx={{ order: { xs: 2, md: 1 } }}>
              <MotionBox
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: { xs: 'center', md: 'flex-start' },
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                <motion.div variants={itemVariants}>
                  <Typography
                    variant={isMobile ? 'h3' : 'h2'}
                    component="h1"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    Find and Track Class Action Settlements in One Place
                  </Typography>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <>
                    <Typography
                      variant="body1"
                      component="h2"
                      sx={{
                        fontWeight: 400,
                        mb: 2,
                        color: 'text.secondary',
                        maxWidth: 600,
                      }}
                    >
                      ClassAct is reimagining the future of class action access.
                      We're building a platform that makes it easy to discover,
                      track, and engage with class action cases — all in one
                      place. Whether you're trying to understand what you're
                      eligible for, or looking for a smarter way to stay
                      informed, ClassAct helps remove the friction and bring
                      clarity to a complex process.
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 400,
                        mb: 4,
                        color: 'text.secondary',
                        maxWidth: 600,
                      }}
                    >
                      We believe class actions should be transparent, intuitive,
                      and accessible to everyone — not just those with legal
                      expertise. Our mission is to make justice more reachable,
                      one case at a time.
                    </Typography>
                  </>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 2,
                      width: '100%',
                      maxWidth: 500,
                      mb: 4,
                    }}
                  >
                    <TextField
                      fullWidth
                      placeholder="Enter your email"
                      variant="outlined"
                      value={email}
                      onChange={handleEmailChange}
                      disabled={loading}
                      required
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon color="primary" />
                            </InputAdornment>
                          ),
                          sx: {
                            borderRadius: '12px',
                            bgcolor: 'background.paper',
                            height: '40px',
                          },
                        },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: '40px',
                        },
                        '& .MuiInputBase-input:-webkit-autofill': {
                          WebkitTextFillColor: theme.palette.text.primary,
                          caretColor: theme.palette.text.primary,
                          borderRadius: 'inherit',
                          '-webkit-transition':
                            'background-color 5000s ease-in-out 0s',
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={loading}
                      sx={{
                        borderRadius: '20px',
                        px: 2,
                        py: 0,
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        height: '40px',
                        minWidth: '130px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={20} />
                      ) : (
                        'Join Mailing List'
                      )}
                    </Button>
                  </Box>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 3,
                      alignItems: 'center',
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <VerifiedIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        User Privacy Protected
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <VerifiedIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Free for Plaintiffs
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <VerifiedIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Secure & Compliant
                      </Typography>
                    </Box>
                  </Box>
                </motion.div>
              </MotionBox>
            </Grid>

            {!isMobile && (
              <Grid size={{ xs: 12, md: 6 }} sx={{ order: { xs: 1, md: 2 } }}>
                <MotionBox
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    component="img"
                    src={logo}
                    alt="ClassAct Application"
                    sx={{
                      width: '75%',
                      display: 'block',
                    }}
                  />
                  {/* </Paper> */}
                </MotionBox>
              </Grid>
            )}
          </Grid>
        </Container>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            variant="filled"
            sx={{ width: '100%', borderRadius: '12px' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    );
  },
);

export default HeroSection;
